import { useState } from "react";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import SignaturePad from "@/components/SignaturePad";
import { PenLine, CheckCircle2, Lock } from "lucide-react";
import { sigRoles, destinataires } from "@/lib/parties";

export default function SignaturesSection({ etat, onSigned, locked }) {
  const [pad, setPad] = useState(null);
  const [busy, setBusy] = useState(false);
  const roles = sigRoles(etat);
  const sigs = etat.signatures || [];
  const missing = roles.filter((r) => !sigs.some((s) => s.role === r.key));
  const emails = destinataires(etat).map((d) => d.email).filter(Boolean);

  const sign = async ({ nom, lieu, image }) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/etats/${etat.id}/signatures`, { role: pad.role, nom, lieu, image });
      setPad(null);
      if (data.complete) {
        const ok = data.envois.filter((e) => e.ok).length;
        toast.success(`Constat signé et verrouillé${ok ? ` · PDF envoyé à ${ok} destinataire${ok > 1 ? "s" : ""}` : ""}`);
      } else toast.success("Signature enregistrée");
      onSigned(data.etat);
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <div className="bg-white rounded-lg border border-dd-light p-5" data-testid="signatures-section">
      <div className="flex items-center justify-between mb-2">
        <h3 className="text-lg font-semibold text-dd-blue">Signatures</h3>
        {locked ? <Badge className="bg-dd-night text-white border-0"><Lock size={12} className="mr-1" /> Signé le {etat.signe_le ? new Date(etat.signe_le).toLocaleDateString("fr-CH") : ""}</Badge>
          : <span className="text-sm text-dd-warm">{missing.length} signature{missing.length > 1 ? "s" : ""} manquante{missing.length > 1 ? "s" : ""}</span>}
      </div>
      {!locked && (
        <p className="text-sm text-dd-warm mb-4">
          Dès que toutes les parties ont signé, le constat est verrouillé et le PDF est envoyé automatiquement
          {emails.length ? ` à : ${emails.join(", ")}` : ""} ainsi qu'au gérant.
          {!emails.length && <span className="text-destructive"> Aucune adresse e-mail saisie dans « Informations générales ».</span>}
        </p>
      )}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {roles.map(({ key: role, label, nom }) => {
          const s = sigs.find((x) => x.role === role);
          return (
            <div key={role} className="border border-dd-light rounded-md p-3" data-testid={`sig-card-${role}`}>
              <div className="text-xs uppercase tracking-wider text-dd-warm">{label}</div>
              <div className="h-24 my-2 flex items-center justify-center bg-dd-light/30 rounded">
                {s ? <img src={s.image} alt="signature" className="max-h-20" /> : <span className="text-xs text-dd-warm">Non signé</span>}
              </div>
              <div className="text-sm font-medium text-dd-night">{s?.nom || nom || "—"}</div>
              {s ? (
                <div className="text-xs text-dd-warm flex items-center gap-1 mt-1"><CheckCircle2 size={13} className="text-dd-blue" /> {s.lieu ? `${s.lieu}, ` : ""}{s.date} à {s.heure}</div>
              ) : !locked && (
                <Button size="sm" onClick={() => setPad({ role, label, nom: nom || "" })} data-testid={`sign-btn-${role}`}
                  className="mt-2 h-11 w-full bg-dd-blue hover:bg-dd-night text-white"><PenLine size={15} className="mr-1" /> Signer</Button>
              )}
            </div>
          );
        })}
      </div>
      <SignaturePad open={!!pad} onOpenChange={(o) => !o && setPad(null)} roleLabel={pad?.label} defaultNom={pad?.nom} onSign={sign} busy={busy} />
    </div>
  );
}
